import React, { useState, useEffect } from 'react';
import { RgbaColorPicker } from 'react-colorful';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { stringToRgba, rgbaToString, RGBAColor } from '@/components/ColorPickerRGBA';

interface CompactColorPickerProps {
  label: string;
  color: string;
  onChange: (color: string) => void;
  showAlpha?: boolean;
  disabled?: boolean;
}

export const CompactColorPicker: React.FC<CompactColorPickerProps> = ({
  label,
  color,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempColor, setTempColor] = useState<RGBAColor>(stringToRgba(color));

  useEffect(() => {
    const parsed = stringToRgba(color);
    setTempColor(parsed);
  }, [color]);

  const handleColorChange = (newColor: RGBAColor) => {
    setTempColor(newColor);
    onChange(rgbaToString(newColor));
  };

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs font-medium text-[#6B7280]">{label}</Label>
      <Popover open={isOpen} onOpenChange={setIsOpen} modal={false}>
        <PopoverTrigger asChild>
          <button
            disabled={disabled}
            className="w-[52px] h-[52px] rounded-[12px] border border-[#D8DBE2] shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:border-[#2D72FF] hover:shadow-[0_2px_6px_rgba(0,0,0,0.08)] transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
            aria-label={label}
          >
            {/* Checkerboard pattern for transparency */}
            <div className="absolute inset-0 bg-[linear-gradient(45deg,#ccc_25%,transparent_25%,transparent_75%,#ccc_75%,#ccc),linear-gradient(45deg,#ccc_25%,transparent_25%,transparent_75%,#ccc_75%,#ccc)] bg-[length:10px_10px] bg-[position:0_0,5px_5px]" />
            {/* Actual color */}
            <div className="absolute inset-0" style={{ backgroundColor: rgbaToString(tempColor) }} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="center"
          sideOffset={8}
          collisionPadding={16}
          className="w-[240px] p-4 rounded-xl shadow-[0_16px_40px_rgba(0,0,0,0.15)] border-0 z-[99999] bg-background"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="space-y-2.5">
            <div className="text-center">
              <h3 className="text-sm font-semibold text-foreground">
                {label}
              </h3>
            </div>

            <div className="flex justify-center">
              <RgbaColorPicker
                color={tempColor}
                onChange={handleColorChange}
                style={{ width: '200px', height: '160px' }}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
